import {
  formatDateTime,
  getTimeDifference,
} from '@airship-platform/airship-patterns';
import { FlexBox } from '@airship-platform/jam-web/components';
import {
  CalendarIcon,
  ExclamationIcon,
  LocationIcon,
  OfficeBuildingIcon,
  PhoneIcon,
  UserCircleIcon,
} from '@airship-platform/jam-web/icons';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import { JSX } from 'react';
import { Appointment } from '../../../../data-access/appointments';

type Tag = {
  key?: string;
  label: string;
  icon?: JSX.Element;
  color?: 'default' | 'softRed' | 'softGreen' | 'softBlue';
};

type CustomerJob = {
  priority: Appointment['job']['priority'];
  tagTypes: Appointment['job']['tagTypes'];
  isNewCustomer: boolean;
};

const generateTags = (job: CustomerJob) => {
  const priorityLabel = [
    [job.priority.charAt(0), job.priority.slice(1).toLowerCase()].join(''),
    'urgency',
  ].join(' ');

  const getChipProps = (
    tag: 'HIGH' | string
  ): { icon?: Tag['icon']; color: Tag['color'] } => {
    return {
      icon:
        tag === 'HIGH' ? <ExclamationIcon height={24} width={24} /> : undefined,
      color: tag === 'HIGH' ? 'softRed' : 'default',
    };
  };

  const tags: Tag[] = [];

  tags.push({
    label: priorityLabel,
    ...getChipProps(job.priority),
  });

  job.tagTypes.forEach((tag) => {
    tags.push({
      key: tag.name,
      label: tag.name,
      ...getChipProps(job.priority),
    });
  });

  if (job.isNewCustomer) {
    tags.push({
      label: job.isNewCustomer ? 'New customer' : 'Existing customer',
      icon: <UserCircleIcon />,
    });
  }

  return tags;
};

type CustomerInfoProps = CustomerJob & {
  startDateTime: string;
  name: string;
  address1: string | null;
  city: string | null;
  state: string | null;
  businessUnitName?: string;
};

const CustomerInfo: React.FC<CustomerInfoProps> = ({
  startDateTime,
  name,
  address1,
  city,
  state,
  priority,
  tagTypes,
  isNewCustomer,
  businessUnitName,
}) => {
  const formattedDate = startDateTime ? formatDateTime(startDateTime) : '';
  const timeDifference = startDateTime ? getTimeDifference(startDateTime) : '';

  const tags = generateTags({ priority, tagTypes, isNewCustomer });

  return (
    <FlexBox flexDirection={'column'} gap={3} flexWrap={'wrap'} width={'100%'}>
      <Typography variant="h2">{name}</Typography>
      <FlexBox gap={1} flexWrap={'wrap'}>
        {tags.map((tag, index) => (
          <Chip
            key={tag.key || `key-${index}`}
            label={tag.label}
            icon={tag.icon}
            color={tag.color}
          />
        ))}
      </FlexBox>
      <FlexBox flexDirection={'column'} gap={1} mb={2}>
        {!!businessUnitName && (
          <FlexBox gap={1} alignItems={'flex-start'}>
            <OfficeBuildingIcon />
            <Typography variant="body2">
              {`Business unit: ${businessUnitName}`}
            </Typography>
          </FlexBox>
        )}
        {!!timeDifference && (
          <FlexBox gap={1} alignItems={'flex-start'}>
            <PhoneIcon />
            <Typography variant="body2">
              {`Customer outreach: ${timeDifference} ago`}
            </Typography>
          </FlexBox>
        )}
        {formattedDate && (
          <FlexBox gap={1} alignItems={'flex-start'}>
            <CalendarIcon />
            <Typography variant="body2">{formattedDate}</Typography>
          </FlexBox>
        )}

        <FlexBox alignItems={'flex-start'} gap={1}>
          <LocationIcon />
          <Typography variant="body2">
            {`${address1}, ${city}, ${state}`}
          </Typography>
        </FlexBox>
      </FlexBox>
    </FlexBox>
  );
};

export default CustomerInfo;
